import type { IPlatformService, AppState } from '../../contracts'
import { safeGetLogger } from '@restaurant/logging'


/**
 * Web implementation of platform service using browser APIs
 */
export class WebPlatformService implements IPlatformService {
  private currentAppState: AppState = 'active'

  constructor() {
    // Initialize app state based on document visibility
    this.currentAppState = document.hidden ? 'background' : 'active'
  }

  onAppStateChange(callback: (state: AppState) => void): () => void {
    const handleVisibilityChange = () => {
      const newState: AppState = document.hidden ? 'background' : 'active'

      if (newState !== this.currentAppState) {
        this.currentAppState = newState
        callback(newState)
      }
    }

    const handlePageFocus = () => {
      if (this.currentAppState !== 'active') {
        this.currentAppState = 'active'
        callback('active')
      }
    }

    const handlePageBlur = () => {
      if (this.currentAppState !== 'background') {
        this.currentAppState = 'background'
        callback('background')
      }
    }

    // Listen to multiple events for comprehensive coverage
    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('focus', handlePageFocus)
    window.addEventListener('blur', handlePageBlur)

    // Return cleanup function
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('focus', handlePageFocus)
      window.removeEventListener('blur', handlePageBlur)
    }
  }

  getCurrentAppState(): AppState {
    return this.currentAppState
  }

  isAppInForeground(): boolean {
    return this.currentAppState === 'active'
  }

  getPlatformInfo() {
    return {
      platform: 'web' as const,
      version: navigator.appVersion,
      userAgent: navigator.userAgent,
    }
  }

  handleSessionTimeout(): void {
    // Web-specific session timeout handling
    // Could show a modal, clear local storage, etc.
    const logger = safeGetLogger()
    logger?.auth('logout', { reason: 'session_timeout', platform: 'web' })

    // Clear any web-specific session data
    if (typeof window !== 'undefined') {
      // Clear sessionStorage but preserve localStorage for Zustand persistence
      sessionStorage.clear()

      // Could dispatch a custom event for other parts of the app to listen to
      window.dispatchEvent(new CustomEvent('sessionTimeout', {
        detail: { timestamp: Date.now() }
      }))
    }
  }

  async isNetworkAvailable(): Promise<boolean> {
    if (typeof navigator !== 'undefined' && 'onLine' in navigator) {
      return navigator.onLine
    }

    // Fallback: try to fetch a small resource
    try {
      await fetch('/favicon.ico', {
        method: 'HEAD',
        cache: 'no-cache',
        mode: 'no-cors'
      })
      return true
    } catch {
      return false
    }
  }

  onNetworkStateChange(callback: (isConnected: boolean) => void): () => void {
    const handleOnline = () => callback(true)
    const handleOffline = () => callback(false)

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    // Return cleanup function
    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }

  async showDatePicker(options: {
    date?: string
    maximumDate?: string
    minimumDate?: string
    mode?: 'date' | 'time' | 'datetime'
  }): Promise<string | null> {
    return new Promise<string | null>((resolve) => {
      const input = document.createElement('input')
      input.type = options.mode === 'time' ? 'time' : 'date'
      input.style.position = 'absolute'
      input.style.left = '-9999px'

      if (options.date) {
        input.value = options.date
      }
      if (options.maximumDate) {
        input.max = options.maximumDate
      }
      if (options.minimumDate) {
        input.min = options.minimumDate
      }

      const cleanup = () => {
        if (input.parentNode) {
          document.body.removeChild(input)
        }
      }

      input.addEventListener('change', () => {
        resolve(input.value || null)
        cleanup()
      })

      input.addEventListener('blur', () => {
        // Small delay to allow change event to fire first
        setTimeout(() => {
          cleanup()
          resolve(null)
        }, 100)
      })

      document.body.appendChild(input)
      input.focus()
      input.click()
    })
  }

  async showAlert(options: {
    title: string
    message: string
    buttonText?: string
  }): Promise<boolean> {
    alert(`${options.title}\n\n${options.message}`)
    return true
  }

  async showConfirm(options: {
    title: string
    message: string
    confirmText?: string
    cancelText?: string
    destructive?: boolean
  }): Promise<boolean> {
    return confirm(`${options.title}\n\n${options.message}`)
  }

  showToast(messageOrOptions: string | {
    message: string
    type?: 'success' | 'error' | 'warning' | 'info'
    duration?: number
  }): void {
    const options =
      typeof messageOrOptions === 'string'
        ? { message: messageOrOptions }
        : messageOrOptions
    const toast = document.createElement('div')
    toast.textContent = options.message
    toast.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${options.type === 'error' ? '#ef4444' : options.type === 'success' ? '#10b981' : options.type === 'warning' ? '#f59e0b' : '#3b82f6'};
      color: white;
      padding: 12px 16px;
      border-radius: 6px;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      z-index: 9999;
      font-family: system-ui, -apple-system, sans-serif;
      font-size: 14px;
    `

    document.body.appendChild(toast)

    setTimeout(() => {
      if (toast.parentNode) {
        document.body.removeChild(toast)
      }
    }, options.duration || 3000)
  }

  hapticFeedback(_type?: 'light' | 'medium' | 'heavy'): void {
    // Haptic feedback is not available in web browsers - no-op
  }

  openUrl(url: string): void {
    window.open(url, '_blank')
  }
}
